import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Search, Add, FilterList } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSearch,
  resetAll,
  setFilter,
} from "../../../../redux/purchaseRequest/purchaseRequestSlice";
import { useGetList } from "../../../../hooks/useGetList";
import { XIcon } from "lucide-react";
import debounce from "../../../../Utils/debounce";

const PurchaseRequestHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.purchaseRequest);

  const { data: branchData, loading: branchLoading } =
    useGetList(`branch/options`);

  const inputRef = useRef();

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Canceled", label: "Canceled" },
  ];

  const handleSearchChange = debounce(() => {
    if (inputRef.current) {
      dispatch(setSearch(inputRef.current.value));
    }
  }, 500);

  const handleBranchChange = (event) => {
    dispatch(setFilter({ relatedBranch: event.target.value }));
  };

  const handleStatusChange = (event) => {
    dispatch(setFilter({ status: event.target.value }));
  };

  const handleClearFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    dispatch(resetAll());
  };

  const handleAddPurchase = () => {
    navigate("/add_purchase");
  };

  const handleClearSearch = () => {
    dispatch(setSearch(""));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const hasActiveFilters =
    filter?.keyword ||
    filter?.filterType?.relatedBranch ||
    filter?.filterType?.status;

  return (
    <Card sx={{ mb: 3, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              inputRef={inputRef}
              placeholder="Search by code"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <Search sx={{ mr: 1, color: "text.secondary" }} />
                ),
                endAdornment: inputRef.current?.value ? (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ ml: 1 }}
                  >
                    <XIcon fontSize="small" size={12} />
                  </IconButton>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          {/* Branch Filter */}
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Branch</InputLabel>
              <Select
                value={filter.filterType?.relatedBranch}
                onChange={handleBranchChange}
                label="Branch"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Branches</MenuItem>
                {!branchLoading &&
                  branchData?.data?.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filter.status}
                onChange={handleStatusChange}
                label="Status"
                sx={{ borderRadius: 2 }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} md={3}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              {hasActiveFilters && (
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={handleClearFilters}
                    color="primary"
                    sx={{ borderRadius: 2 }}
                  >
                    <XIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddPurchase}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Add Purchase
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            <FilterList sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="caption" color="textSecondary">
              Active filters:
            </Typography>

            {filter?.keyword && (
              <Chip
                label={`Search: ${filter.keyword}`}
                size="small"
                onDelete={handleClearSearch}
                color="primary"
                variant="outlined"
              />
            )}

            {filter?.filterType?.relatedBranch && (
              <Chip
                label={`Branch: ${branchData?.data?.find((b) => b._id === filter.filterType.relatedBranch)?.name || filter.filterType.relatedBranch}`}
                size="small"
                onDelete={() => dispatch(setFilter({ relatedBranch: "" }))}
                color="primary"
                variant="outlined"
              />
            )}

            {filter?.filterType?.status && (
              <Chip
                label={`Status: ${filter.filterType.status}`}
                size="small"
                onDelete={() => dispatch(setFilter({ status: "" }))}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PurchaseRequestHeader;
